import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    LinearProgress,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    alpha,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublicIcon from "@mui/icons-material/Public";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { API_URL, apiFetch } from "../config";
import GlowCard from "./ui/GlowCard";
import SessionsAreaChart from "./analytics/SessionsAreaChart";
import TrafficSourcesChart from "./analytics/TrafficSourcesChart";
import WorldSessionsMap from "./analytics/WorldSessionsMap";

const WIX_ANALYTICS_API = `${API_URL}/integrations/wix/analytics`;

const regionNames =
    typeof Intl !== "undefined" && Intl.DisplayNames
        ? new Intl.DisplayNames(["en"], { type: "region" })
        : null;

function countryLabel(code) {
    if (!code) return "Unknown";
    try {
        return regionNames?.of(code) || code;
    } catch {
        return code;
    }
}

function fmtNum(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(v);
}

function fmtPct(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return `${(v <= 1 ? v * 100 : v).toFixed(1)}%`;
}

function MetricTile({ label, value, caption }) {
    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: 1,
                borderColor: "divider",
                bgcolor: "action.hover",
                height: "100%",
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.35 }}>
                {value}
            </Typography>
            {caption ? (
                <Typography variant="caption" color="text.secondary">
                    {caption}
                </Typography>
            ) : null}
        </Box>
    );
}

function AnalyticsPanel({ title, children }) {
    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 280 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                {title}
            </Typography>
            <Box sx={{ flex: 1 }}>{children}</Box>
        </Box>
    );
}

export default function SiteAnalyticsCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (refresh = false) => {
        if (refresh) setRefreshing(true);
        else setLoading(true);
        try {
            const url = refresh ? `${WIX_ANALYTICS_API}?refresh=true` : WIX_ANALYTICS_API;
            const res = await apiFetch(url);
            const json = await res.json();
            setData(json);
        } catch (e) {
            setData({ configured: false, error: e.message || "Failed to load analytics." });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        load(false);
    }, [load]);

    const display = useMemo(() => {
        if (!data) return null;
        if (data.error && data.staleCache) return { ...data.staleCache, _stale: true, _error: data.error, _permissionHint: data.permissionHint };
        if (data.error) return { _errorOnly: true, ...data };
        return data;
    }, [data]);

    const p7 = display?.periods?.last7Days;
    const p30 = display?.periods?.last30Days;
    const sources = display?.trafficSources?.length
        ? display.trafficSources
        : (display?.topReferrers || []).map((r) => ({
            label: r.category,
            source: r.category,
            sessions: r.sessions,
            views: r.views,
        }));

    const maxCountrySessions = Math.max(1, ...(display?.topCountries || []).map((c) => c.sessions || 0));

    return (
        <GlowCard hover={false}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Site analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {display?.siteUrl || "pointblankrobotics.com"} · Wix traffic
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        component={Link}
                        href="https://manage.wix.com/dashboard"
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon sx={{ fontSize: "1rem !important" }} />}
                    >
                        Traffic overview
                    </Button>
                    <Button
                        size="small"
                        variant="text"
                        startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={() => load(true)}
                        disabled={loading || refreshing}
                    >
                        Refresh
                    </Button>
                </Stack>
            </Stack>

            {loading && !display ? (
                <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                </Box>
            ) : null}

            {display?._errorOnly && !display?.periods ? (
                <Alert severity={display.code === "WIX_NOT_CONFIGURED" ? "info" : "warning"}>
                    {display.error}
                    {display.permissionHint ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {display.permissionHint}
                        </Typography>
                    ) : null}
                </Alert>
            ) : null}

            {display?._stale ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Showing cached data — latest fetch failed: {display._error}
                </Alert>
            ) : null}

            {display?.periods ? (
                <>
                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <MetricTile label="Sessions (7d)" value={fmtNum(p7?.sessions)} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <MetricTile label="Page views (7d)" value={fmtNum(p7?.views)} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <MetricTile label="Sessions (30d)" value={fmtNum(p30?.sessions)} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <MetricTile
                                label="Bounce rate (30d)"
                                value={fmtPct(p30?.bounceRate)}
                                caption={p30?.bounceRate == null ? "Not enough data" : undefined}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Get to know your visitors
                    </Typography>
                    <Grid container spacing={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden", mb: 2.5 }}>
                        <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2, borderRight: { md: 1 }, borderColor: "divider" }}>
                            <AnalyticsPanel title="Sessions over time">
                                <SessionsAreaChart series={display.sessionsOverTime || []} />
                            </AnalyticsPanel>
                        </Grid>
                        <Grid
                            size={{ xs: 12, md: 4 }}
                            sx={{
                                p: 2,
                                borderRight: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <AnalyticsPanel title="Top traffic sources">
                                <TrafficSourcesChart sources={sources || []} />
                            </AnalyticsPanel>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2, borderTop: { xs: 1, md: 0 }, borderColor: "divider" }}>
                            <AnalyticsPanel title="Sessions by location">
                                <WorldSessionsMap countries={display.mapCountries || display.topCountries || []} />
                            </AnalyticsPanel>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                <PublicIcon fontSize="small" color="primary" />
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Top locations (30d)
                                </Typography>
                            </Stack>
                            {(display.topCountries || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No location breakdown yet.
                                </Typography>
                            ) : (
                                <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                                                <TableCell>Country</TableCell>
                                                <TableCell align="right">Sessions</TableCell>
                                                <TableCell align="right">Views</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {display.topCountries.map((row) => (
                                                <TableRow key={row.countryCode} hover>
                                                    <TableCell>
                                                        <Stack spacing={0.5}>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {countryLabel(row.countryCode)}
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={Math.min(100, ((row.sessions || 0) / maxCountrySessions) * 100)}
                                                                sx={(theme) => ({
                                                                    height: 4,
                                                                    borderRadius: 1,
                                                                    maxWidth: 200,
                                                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                                    "& .MuiLinearProgress-bar": {
                                                                        borderRadius: 1,
                                                                        bgcolor: "primary.main",
                                                                    },
                                                                })}
                                                            />
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="right">{fmtNum(row.sessions)}</TableCell>
                                                    <TableCell align="right">{fmtNum(row.views)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                <TrendingUpIcon fontSize="small" color="primary" />
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Traffic sources detail (30d)
                                </Typography>
                            </Stack>
                            {(sources || []).length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No referrer data yet.
                                </Typography>
                            ) : (
                                <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                                                <TableCell>Source</TableCell>
                                                <TableCell align="right">Sessions</TableCell>
                                                <TableCell align="right">Views</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sources.map((row) => (
                                                <TableRow key={`${row.source}-${row.label}`} hover>
                                                    <TableCell sx={{ fontWeight: 600 }}>{row.label || row.source}</TableCell>
                                                    <TableCell align="right">{fmtNum(row.sessions)}</TableCell>
                                                    <TableCell align="right">{fmtNum(row.views)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Grid>
                    </Grid>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                        {display.cached ? "Cached" : "Live"} · Time zone: {display.timezone || "site default"}
                        {display.fetchedAt ? ` · Updated ${new Date(display.fetchedAt).toLocaleString()}` : ""}
                    </Typography>
                </>
            ) : null}
        </GlowCard>
    );
}
